import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { DynamicDocumentService } from '../../services/dynamic-document.service';
import { GeneralDynamicContent } from '../../entities/general-dynamic-content';
import { CustomTranslateService } from '../../services/custom-translate.service';
import { Subscription, catchError, map } from 'rxjs';
import { DynamicResourceService } from '../../services/dynamic-resource.service';
import { DomSanitizer } from '@angular/platform-browser';
import { PATHS } from 'src-befor/app-routing.module';
import { MetaService } from 'src-befor/services/meta.service';
import { RouterService } from '../../services/router.service';

@Component({
  selector: 'app-general-dynamic-content',
  templateUrl: './general-dynamic-content.component.html',
  styleUrls: ['./general-dynamic-content.component.css'],
})
export class GeneralDynamicContentComponent implements OnInit, OnDestroy {
  routes: any = PATHS;

  templates = [
    {
      page: 'technologies',
      pageTranslates: [
        this.routes.technologies_en,
        this.routes.technologies_fr,
        this.routes.technologies_es,
        this.routes.technologies_de,
      ],
      name: 'Menu.technologies',
      title: 'Technologies.title',
      more: 'Technologies.more',
      documentName: 'dynamic_technology',
    },
    {
      page: 'news',
      pageTranslates: [
        this.routes.news_en,
        this.routes.news_fr,
        this.routes.news_es,
        this.routes.news_de,
      ],
      name: 'Menu.actualites',
      title: 'News.title',
      more: 'News.more',
      documentName: 'dynamic_news',
    },
    {
      page: 'company',
      pageTranslates: [
        this.routes.company_en,
        this.routes.company_fr,
        this.routes.company_es,
        this.routes.company_de,
      ],
      name: 'Menu.societe',
      title: 'Company.title',
      more: 'Company.more',
      documentName: 'dynamic_company',
    },
  ];

  dynamicContentList: GeneralDynamicContent[] = [];
  selectedContent: GeneralDynamicContent;

  selectedTemplate: (typeof this.templates)[0];

  listMode: boolean = true;

  loadedRoute: boolean = false;
  noContent: boolean;

  routeSubscription = new Subscription();

  get others() {
    return this.selectedTemplate?.page == 'company'
      ? []
      : this.dynamicContentList.filter(
        (el) => el?.id !== this.selectedContent?.id
      );
  }

  get emptyList() {
    return this.dynamicContentList.length == 0;
  }

  get currentLang() {
    return "fr";
  }

  constructor(
    private router: Router,
    private routerService: RouterService,
    private metaService: MetaService,
    private dynamicDocumentService: DynamicDocumentService,
    private sanitizer: DomSanitizer,
    private dynamicResourceService: DynamicResourceService,
    public translateService: CustomTranslateService
  ) {
    this.watchRoute();
    this.watchLang();
  }

  watchRoute() {
    this.routeSubscription = this.router.events.pipe().subscribe((ev) => {
      if (ev instanceof NavigationEnd && this.loadedRoute) {
        this.loadedRoute = false;
        this.ngOnInit();
        window.scrollTo(0, 0);
      }
    });
  }

  watchLang() {
    this.translateService.onLangChange.subscribe(() => {
      if (this.loadedRoute) {
        this.ngOnInit();
      }
    });
  }

  ngOnInit(): void {
    this.selectTemplate();
  }

  selectTemplate() {
    let urlArray = this.router.url.split('/');
    let temp = this.templates.find((el) =>
      el?.pageTranslates.includes(urlArray[2])
    );
    if (temp) this.selectedTemplate = temp;

    this.listMode = this.selectedTemplate
      ? this.selectedTemplate.pageTranslates.includes(
        urlArray[urlArray.length - 1]
      )
      : true;

    if (this.selectedTemplate?.page == 'company') this.listMode = false;
    this.loadContents();
  }

  loadContents() {
    let filters = [
      {
        fieldName: 'status',
        conditions: [{ type: 'EQUALS', value: 'VALIDE' }],
      },
      {
        fieldName: 'lang',
        conditions: [
          { type: 'EQUALS', value: this.translateService.currentLang },
        ],
      },
    ];

    if (this.selectedTemplate?.page == 'company') {
      // remove langs if company page
      filters = filters.filter((el) => el.fieldName != 'lang');
    }

    this.dynamicDocumentService
      .find(
        {
          collectionName: this.selectedTemplate?.documentName,
          filters: filters,
          sorts: { publishedOrder: 'DESC' },
          page: 1,
          pageSize: 150,
        },
        true
      )
      .subscribe((res: any) => {
        this.loadedRoute = true;
        if (res?.content?.length > 0) this.dynamicContentList = res.content;

        this.selectContent();
      });
  }

  getTranslatedPage(page: string) {
    return this.routes[`${page}_${this.currentLang}`];
  }

  selectContent() {
    let urlArray = this.router.url.split('/');
    let content = this.dynamicContentList.find(
      (el) => el.url === urlArray[urlArray.length - 1]
    );

    if (!this.listMode && content) this.selectedContent = content;

    if (this.selectedTemplate.page == 'company') {
      // SELECT CURRENTLANGUAGE ELEMENT
      let element = this.dynamicContentList.find(
        (el) => el.lang == this.translateService.currentLang
      );
      if (element) this.selectedContent = element;
    }

    if (this.dynamicContentList.length == 0 && !this.selectedContent) {
      this.noContent = true;
    }

    if (this.selectedContent?.reference) {
      this.metaService.setTitle(this.selectedContent.reference);
      this.metaService.setMeta(this.selectedContent.reference);
    }

    this.loadHTML();
    this.loadVideos();
    this.loadDocuments();
  }

  getContent(contentRef: string) {
    return this.selectedContent?.content.find(
      (el: any) => el.ref === contentRef
    )?.content;
  }

  getTextContent(contentRef: string) {
    return this.sanitizer.bypassSecurityTrustHtml(
      this.selectedContent?.content.find((el: any) => el.ref === contentRef)
        ?.content
    );
  }

  getType(contentRef: string) {
    return this.selectedContent?.content.find(
      (el: any) => el.ref === contentRef
    ).type;
  }

  openOther(url?: string) {
    if (this.selectedContent.url && url)
      this.router.navigate([
        this.router.url.replace(this.selectedContent?.url, url),
      ]);
  }

  loadHTML() {
    setTimeout(() => {
      let htmlElements = document.getElementsByClassName('html-content');
      Array.from(htmlElements).forEach((el) => {
        if (el?.id) this.displayHTML(el.id);
      });
    }, 100);
  }

  displayHTML(ref: string) {
    let content = this.getContent(ref);

    let div = document.getElementById(ref);
    if (div) div.innerHTML = content;
  }

  loadVideos() {
    setTimeout(() => {
      let videoElements = document.getElementsByClassName('iframe');
      Array.from(videoElements).forEach((el) => {
        if (el?.id) this.embedYouTubeVideo(el.id);
      });
    }, 100);
  }

  loadDocuments() {
    setTimeout(() => {
      let docElements = document.getElementsByClassName('document');
      Array.from(docElements).forEach((el) => {
        if (el?.id) this.setDocument(el.id);
      });
    }, 100);
  }

  setDocument(ref: string) {
    // elementRef id = documentId
    this.dynamicResourceService
      .findById(ref, true)
      .pipe(
        map((url: string) => {
          const docSrc = url;
          if (docSrc) {
            // Create iframe element
            const div = document.createElement('a');
            div.className = 'doc-element';
            div.innerHTML = 'Ouvrir le document';
            div.href = docSrc;
            div.target = '_blank';

            // Append iframe to the div with id 'iframe'
            const divContainer = document.getElementById(ref);
            if (divContainer) {
              divContainer.innerHTML = ''; // Clear any existing content
              divContainer.appendChild(div);
            }
          }
        }),
        catchError((error) => {
          let err: string = error?.error ? error?.error.text : error.text;
          if (err) {
            const div = document.createElement('a');
            div.className = 'doc-element';
            div.innerHTML = 'Ouvrir le document';
            div.href = err;
            div.target = '_blank';

            // Append iframe to the div with id 'iframe'
            const divContainer = document.getElementById(ref);
            if (divContainer) {
              divContainer.innerHTML = ''; // Clear any existing content
              divContainer.appendChild(div);
            }
          }
          throw error;
        })
      )
      .subscribe();
  }

  embedYouTubeVideo(ref: string): void {
    // Extract video ID from YouTube URL
    const videoSrc = this.transformYTUrl(this.getContent(ref));

    if (videoSrc) {
      // Create iframe element
      const iframe = document.createElement('iframe');
      iframe.src = videoSrc;
      iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope;';
      iframe.allowFullscreen = true;
      iframe.className = 'iframe-element';

      // Append iframe to the div with id 'iframe'
      const iframeContainer = document.getElementById(ref);
      if (iframeContainer) {
        iframeContainer.innerHTML = ''; // Clear any existing content
        iframeContainer.appendChild(iframe);
      }
    }
  }

  transformYTUrl(url: string) {
    return `https://youtube.com/embed/${this.getYouTubeVideoId(url)}`;
  }

  // Function to extract YouTube video ID from the link
  private getYouTubeVideoId(link: string): string | null {
    // Regular expression to extract video ID from various YouTube link formats
    const regex =
      /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = link.match(regex);
    return match ? match[1] : null;
  }

  getTranslatedRoute(route?: string) {
    return this.routerService.getTranslatedRoute(route);
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
  }
}
